import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { workspaceService, memberService } from '../../lib/resellerService';

const ROLE_COLORS = {
  admin: 'bg-purple-500/10 text-purple-400',
  editor: 'bg-blue-500/10 text-blue-400',
  super_admin: 'bg-amber-500/10 text-amber-400',
};

const ResellerClients = () => {
  const { userProfile } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'editor' });
  const [inviteError, setInviteError] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: ws, error: wsError } = await workspaceService?.getMyWorkspace();
      if (wsError) throw wsError;
      setWorkspace(ws);
      if (ws?.id) {
        const { data: mems, error: memsError } = await memberService?.getByWorkspace(ws?.id);
        if (memsError) throw memsError;
        setMembers(mems || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e?.preventDefault();
    if (!inviteData?.email?.trim()) { setInviteError('Email is required'); return; }
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(false);
    try {
      const { error } = await memberService?.inviteByEmail(workspace?.id, inviteData?.email?.trim(), inviteData?.role);
      if (error) throw error;
      setInviteSuccess(true);
      setInviteData({ email: '', role: 'editor' });
      await loadData();
      setTimeout(() => { setShowInviteForm(false); setInviteSuccess(false); }, 1500);
    } catch (err) {
      setInviteError(err?.message || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    setUpdatingRoleId(memberId);
    try {
      const { error } = await memberService?.updateRole(memberId, newRole);
      if (error) throw error;
      setMembers(prev => prev?.map(m => m?.id === memberId ? { ...m, role: newRole } : m));
    } catch (err) {
      alert(err?.message || 'Failed to update role');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleRemove = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName || 'this member'} from your workspace?`)) return;
    setRemovingId(memberId);
    try {
      const { error } = await memberService?.remove(memberId);
      if (error) throw error;
      setMembers(prev => prev?.filter(m => m?.id !== memberId));
    } catch (err) {
      alert(err?.message || 'Failed to remove member');
    } finally {
      setRemovingId(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name?.split(' ')?.map(n => n?.[0])?.slice(0, 2)?.join('')?.toUpperCase();
  };

  return (
    <div className="app-layout">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
      <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header userName={userProfile?.fullName || userProfile?.full_name || ''} userRole={userProfile?.role || ''} />
        <main className="content-area">
          <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Icon name="Users" size={20} className="text-purple-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Team Members</h1>
                  <p className="text-sm text-muted-foreground">{workspace?.name || 'Your workspace'}</p>
                </div>
              </div>
              {workspace && (
                <button
                  onClick={() => { setShowInviteForm(true); setInviteError(null); setInviteSuccess(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Icon name="UserPlus" size={16} />
                  Invite Member
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Icon name="Loader2" size={32} className="text-primary animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
                <Icon name="AlertCircle" size={24} className="text-destructive mx-auto mb-2" />
                <p className="text-destructive text-sm">{error}</p>
                <button onClick={loadData} className="mt-3 text-sm text-primary hover:underline">Try again</button>
              </div>
            ) : !workspace ? (
              <div className="bg-card border border-border rounded-xl p-10 text-center">
                <Icon name="Building2" size={40} className="text-muted-foreground mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-1">No Workspace Found</h3>
                <p className="text-sm text-muted-foreground">Contact your Super Admin to set up your reseller workspace.</p>
              </div>
            ) : (
              <>
                {/* Info Banner */}
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <span className="text-foreground font-medium">Invite clients to your workspace.</span> Members you invite can only access your workspace — they cannot see other resellers or platform-level settings. Admins can manage content; Editors can only edit.
                  </div>
                </div>

                {/* Members List */}
                {members?.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-10 text-center">
                    <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-foreground mb-1">No Members Yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">Invite your first client or team member.</p>
                    <button
                      onClick={() => setShowInviteForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      <Icon name="UserPlus" size={16} />
                      Invite Member
                    </button>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-border">
                      <p className="text-sm text-muted-foreground">{members?.length} member{members?.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="divide-y divide-border">
                      {members?.map((member) => (
                        <div key={member?.id} className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors">
                          {/* Avatar */}
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-semibold text-primary">
                            {getInitials(member?.user?.fullName || member?.user?.full_name)}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm truncate">
                              {member?.user?.fullName || member?.user?.full_name || 'Unknown User'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{member?.user?.email}</p>
                          </div>
                          {/* Role selector */}
                          <div className="flex items-center gap-2">
                            {updatingRoleId === member?.id ? (
                              <Icon name="Loader2" size={14} className="text-muted-foreground animate-spin" />
                            ) : (
                              <select
                                value={member?.role}
                                onChange={e => handleRoleChange(member?.id, e?.target?.value)}
                                className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 ${ROLE_COLORS?.[member?.role] || 'bg-muted text-muted-foreground'}`}
                              >
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                              </select>
                            )}
                          </div>
                          {/* Status */}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${member?.user?.isActive !== false ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                            {member?.user?.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                          {/* Remove */}
                          <button
                            onClick={() => handleRemove(member?.id, member?.user?.fullName || member?.user?.full_name)}
                            disabled={removingId === member?.id}
                            className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded"
                            title="Remove member"
                          >
                            {removingId === member?.id ? (
                              <Icon name="Loader2" size={15} className="animate-spin" />
                            ) : (
                              <Icon name="UserMinus" size={15} />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {/* Invite Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Invite Team Member</h2>
                <p className="text-xs text-muted-foreground mt-0.5">They must already have an account on the platform</p>
              </div>
              <button onClick={() => setShowInviteForm(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleInvite} className="p-6 space-y-4">
              {inviteError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                  {inviteError}
                </div>
              )}
              {inviteSuccess && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-sm text-green-400 flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} />
                  Member added successfully!
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                <input
                  type="email"
                  value={inviteData?.email}
                  onChange={e => setInviteData(prev => ({ ...prev, email: e?.target?.value }))}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
                <select
                  value={inviteData?.role}
                  onChange={e => setInviteData(prev => ({ ...prev, role: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="editor">Editor — Can edit content only</option>
                  <option value="admin">Admin — Can manage content and settings</option>
                </select>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Role Permissions</p>
                <p><span className="text-purple-400 font-medium">Admin:</span> Manage pages, media, SEO, and settings within your workspace</p>
                <p className="mt-1"><span className="text-blue-400 font-medium">Editor:</span> Create and edit content only — no settings access</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting || inviteSuccess}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {inviting && <Icon name="Loader2" size={14} className="animate-spin" />}
                  {inviteSuccess ? 'Added!' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerClients;
